import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../atoms/Button';
import { Input } from '../../atoms/Input';
import { Select } from '../../atoms/Select';
import { VisibilitySelector } from '../../molecules/VisibilitySelector';

export type NoteType = 'operational' | 'risk' | 'milestone' | 'general';
export type ImpactLevel = 'no-impact' | 'minor-risk' | 'delay-expected';
export type DelayOption = '+1d' | '+2d' | 'custom' | 'none';
export type Visibility = 'internal' | 'customer-visible';
export type RiskCause = 'supplier' | 'design-dependency' | 'capacity' | 'quality' | 'external' | 'other';

export interface AddNotePayload {
  noteType: NoteType;
  content: string;
  impactLevel: ImpactLevel;
  delay: DelayOption;
  delayCustom?: string;
  affectsStage?: string;
  visibility: Visibility;
  riskCause?: RiskCause;
  notifications?: {
    internal: boolean;
    customer: boolean;
    ops: boolean;
    send: boolean;
  };
  nextAction?: string;
  nextActionDueBy?: string;
  attachments?: File[];
  author?: string;
  [key: string]: any; // Allow additional context fields from parent
}

export interface AddNoteProps {
  /** Header title for the form */
  title?: string;
  /** Helper text under the title */
  subtitle?: string;
  /** Context fields to display (e.g., orderId, stage, health) */
  contextFields?: Record<string, string | React.ReactNode>;
  /** Current user name for owner field */
  currentUser?: string;
  /** Called when Save or Save & Notify is clicked */
  onSave?: (payload: AddNotePayload) => void;
  /** Called when Cancel is clicked */
  onCancel?: () => void;
  /** Show Next Action section */
  showNextAction?: boolean;
  /** Show Attachments section */
  showAttachments?: boolean;
  /** Show Root Cause section (for risk notes) */
  showRootCause?: boolean;
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Helper = styled.div<{ $subtle?: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SectionSubtitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ContextBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ContextField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const ContextLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ContextValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surface};
  resize: vertical;
`;

const RadioRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
`;

const RadioChip = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.gray300)};
  background: ${({ theme, $active }) => ($active ? `${theme.colors.primary}10` : theme.colors.surface)};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ImpactOptions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const ImpactCard = styled.button<{ $active: boolean; $color: string }>`
  border: 2px solid ${({ $active, $color, theme }) => ($active ? $color : theme.colors.gray300)};
  background: ${({ $active, $color, theme }) => ($active ? `${$color}15` : theme.colors.surface)};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  min-width: 220px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};

  &:hover {
    border-color: ${({ $color }) => $color};
  }
`;

const ImpactIcon = styled.div`
  font-size: 20px;
`;

const ImpactLabel = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const ReasonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ReasonChip = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? `${theme.colors.primary}10` : theme.colors.surface)};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FileDropZone = styled.div<{ $isDragActive: boolean }>`
  border: 2px dashed ${({ theme, $isDragActive }) => ($isDragActive ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ theme, $isDragActive }) => ($isDragActive ? `${theme.colors.primary}08` : 'transparent')};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: ${({ theme }) => theme.colors.error}10;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 3px solid ${({ theme }) => theme.colors.error};
`;

const riskCauseOptions: { key: RiskCause; label: string }[] = [
  { key: 'supplier', label: 'Supplier' },
  { key: 'design-dependency', label: 'Design dependency' },
  { key: 'capacity', label: 'Capacity' },
  { key: 'quality', label: 'Quality' },
  { key: 'external', label: 'External' },
  { key: 'other', label: 'Other' },
];

export const AddNote: React.FC<AddNoteProps> = ({
  title = 'Add Note',
  subtitle = 'Capture important updates. Control visibility and notify stakeholders.',
  contextFields,
  currentUser = '',
  onSave,
  onCancel,
  showNextAction = true,
  showAttachments = true,
  showRootCause = true,
}) => {
  const [noteType, setNoteType] = useState<NoteType>('operational');
  const [note, setNote] = useState('');
  const [impact, setImpact] = useState<ImpactLevel>('no-impact');
  const [delay, setDelay] = useState<DelayOption>('none');
  const [delayCustom, setDelayCustom] = useState('');
  const [affectsStage, setAffectsStage] = useState('');
  const [owner, setOwner] = useState(currentUser);
  const [visibility, setVisibility] = useState<Visibility>('internal');
  const [riskCause, setRiskCause] = useState<RiskCause | undefined>();
  const [notifyInternal, setNotifyInternal] = useState(true);
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [notifyOps, setNotifyOps] = useState(false);
  const [nextAction, setNextAction] = useState('');
  const [nextActionDueBy, setNextActionDueBy] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const impactColors = useMemo(
    () => ({
      'no-impact': '#16a34a',
      'minor-risk': '#eab308',
      'delay-expected': '#ef4444',
    }),
    []
  );

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const list = Array.from(files).slice(0, 5 - attachments.length);
    setAttachments((prev) => [...prev, ...list]);
  };

  const removeFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleSubmit = (notify: boolean) => {
    if (!note.trim()) {
      setError('Note content is required.');
      return;
    }
    setError(null);

    const payload: AddNotePayload = {
      noteType,
      content: note.trim(),
      impactLevel: impact,
      delay: impact === 'delay-expected' ? delay : 'none',
      delayCustom: impact === 'delay-expected' ? delayCustom : '',
      affectsStage: impact === 'delay-expected' ? affectsStage : '',
      visibility,
      riskCause: noteType === 'risk' ? riskCause : undefined,
      notifications: {
        internal: notifyInternal,
        customer: notifyCustomer && visibility === 'customer-visible',
        ops: notifyOps,
        send: notify,
      },
      nextAction: nextAction || undefined,
      nextActionDueBy: nextActionDueBy || undefined,
      attachments: attachments.length > 0 ? attachments : undefined,
      author: owner || undefined,
    };

    onSave?.(payload);
  };

  return (
    <Panel>
      <Header>
        <SectionTitle>{title}</SectionTitle>
        <Helper>{subtitle}</Helper>
      </Header>

      {contextFields && Object.keys(contextFields).length > 0 && (
        <ContextBlock>
          {Object.entries(contextFields).map(([key, value]) => (
            <ContextField key={key}>
              <ContextLabel>{key}</ContextLabel>
              <ContextValue>{value}</ContextValue>
            </ContextField>
          ))}
        </ContextBlock>
      )}

      <Section>
        <SectionSubtitle>Note Type</SectionSubtitle>
        <RadioRow>
          {([
            { key: 'operational', label: 'Operational Update' },
            { key: 'risk', label: 'Risk / Issue' },
            { key: 'milestone', label: 'Milestone Completed' },
            { key: 'general', label: 'General Comment' },
          ] as { key: NoteType; label: string }[]).map((opt) => (
            <RadioChip key={opt.key} $active={noteType === opt.key} onClick={() => setNoteType(opt.key)}>
              {opt.label}
            </RadioChip>
          ))}
        </RadioRow>
      </Section>

      <Section>
        <SectionSubtitle>What happened?</SectionSubtitle>
        <TextArea
          placeholder="Be specific. Include context, who is affected, and what was done."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Section>

      <VisibilitySelector
        value={visibility}
        onChange={setVisibility}
        label="Visibility"
        showHelper={true}
      />

      {noteType === 'risk' && showRootCause && (
        <Section>
          <SectionSubtitle>Root Cause (optional)</SectionSubtitle>
          <Helper>What caused this issue? Helps with analytics and retrospectives.</Helper>
          <ReasonGrid>
            {riskCauseOptions.map((opt) => (
              <ReasonChip
                key={opt.key}
                $active={riskCause === opt.key}
                onClick={() => setRiskCause(riskCause === opt.key ? undefined : opt.key)}
              >
                {opt.label}
              </ReasonChip>
            ))}
          </ReasonGrid>
        </Section>
      )}

      <Section>
        <SectionSubtitle>Impact Assessment</SectionSubtitle>
        <Helper>How does this affect the order?</Helper>
        <ImpactOptions>
          {([
            { key: 'no-impact', label: 'No impact', icon: '✓' },
            { key: 'minor-risk', label: 'Minor risk (monitor)', icon: '⚠' },
            { key: 'delay-expected', label: 'Delay expected', icon: '⏱' },
          ] as { key: ImpactLevel; label: string; icon: string }[]).map((opt) => (
            <ImpactCard
              key={opt.key}
              $active={impact === opt.key}
              $color={impactColors[opt.key]}
              onClick={() => setImpact(opt.key)}
            >
              <ImpactIcon>{opt.icon}</ImpactIcon>
              <ImpactLabel>{opt.label}</ImpactLabel>
            </ImpactCard>
          ))}
        </ImpactOptions>

        {impact === 'delay-expected' && (
          <FieldGrid style={{ marginTop: '12px' }}>
            <Select
              label="Expected delay"
              items={[
                { label: '+1 day', value: '+1d' },
                { label: '+2 days', value: '+2d' },
                { label: 'Custom', value: 'custom' },
              ]}
              value={delay}
              onChange={(e) => setDelay(e.target.value as DelayOption)}
            />
            {delay === 'custom' && (
              <Input
                label="Custom delay (e.g., +3 days)"
                value={delayCustom}
                onChange={(e) => setDelayCustom(e.target.value)}
              />
            )}
            <Input
              label="Affects next stage"
              placeholder="Manufacturing / Testing / Loading"
              value={affectsStage}
              onChange={(e) => setAffectsStage(e.target.value)}
            />
          </FieldGrid>
        )}
      </Section>

      <Section>
        <SectionSubtitle>Responsibility & Notifications</SectionSubtitle>
        <FieldGrid>
          <Input label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
        </FieldGrid>
        <Helper>Notify</Helper>
        <RadioRow>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={notifyInternal} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotifyInternal(e.target.checked)} />
            Internal team
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={notifyCustomer}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotifyCustomer(e.target.checked)}
              disabled={visibility === 'internal'}
            />
            Customer
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={notifyOps} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotifyOps(e.target.checked)} />
            Operations lead
          </label>
        </RadioRow>
        {visibility === 'customer-visible' && (
          <Helper style={{ marginTop: '8px', color: '#0891b2', fontWeight: 500 }}>
            ✓ This note is visible to the customer
          </Helper>
        )}
      </Section>

      {showNextAction && (
        <Section>
          <SectionSubtitle>Next Action (optional)</SectionSubtitle>
          <Helper>What should happen next? Updates the order's Next Action field.</Helper>
          <FieldGrid>
            <Input
              label="Next action"
              placeholder="e.g., Expedite production, Schedule vessel"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
            />
            <Input
              label="Due by (when should this happen?)"
              type="datetime-local"
              value={nextActionDueBy}
              onChange={(e) => setNextActionDueBy(e.target.value)}
            />
          </FieldGrid>
        </Section>
      )}

      {showAttachments && (
        <Section>
          <SectionSubtitle>Attachments</SectionSubtitle>
          <Helper>Drag & drop or click to upload. Max 5 files (CAD, PDF, Image, Email).</Helper>
          <FileDropZone
            $isDragActive={isDragActive}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              const input = e.currentTarget.querySelector('input') as HTMLInputElement;
              input?.click();
            }}
          >
            <div style={{ fontSize: '32px' }}>📎</div>
            <div style={{ fontWeight: 600, marginTop: '8px' }}>Drag files here or click to browse</div>
            <Helper style={{ marginTop: '4px' }}>{attachments.length}/5 files selected</Helper>
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFiles(e.target.files)}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                const el = e.currentTarget as HTMLInputElement;
                el.value = '';
              }}
            />
          </FileDropZone>
          {attachments.length > 0 && (
            <FileList>
              {attachments.map((file, idx) => (
                <FileItem key={idx}>
                  <span>{file.name}</span>
                  <button
                    onClick={() => removeFile(idx)}
                    style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#ef4444', fontWeight: 'bold' }}
                  >
                    ✕
                  </button>
                </FileItem>
              ))}
            </FileList>
          )}
        </Section>
      )}

      {error && <ErrorText>{error}</ErrorText>}

      <ActionsRow>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="secondary" onClick={() => handleSubmit(false)}>
          Save Note
        </Button>
        <Button variant="default" onClick={() => handleSubmit(true)}>
          Save & Notify
        </Button>
      </ActionsRow>
    </Panel>
  );
};
